import type { MinimapTone } from "./minimap.ts";
import type { HorizontalBounds } from "../world/geometry.ts";

export const FULL_MAP_OVERVIEW_MANIFEST_URL =
  "/assets/maps/vilnius-overview.json";

interface FullMapOverviewManifestV1 {
  readonly schemaVersion: 1;
  readonly aoiId: string;
  readonly bounds: HorizontalBounds;
  readonly width: number;
  readonly height: number;
  readonly images: Readonly<Record<MinimapTone, string>>;
  readonly neighborhoods?: readonly FullMapNeighborhood[];
  readonly sourceIds: readonly string[];
}

export interface FullMapNeighborhood {
  readonly id: string;
  readonly label: string;
  readonly position: Readonly<{ x: number; z: number }>;
  readonly sampleCount: number;
  readonly sourceId: string;
}

export interface FullMapOverview {
  readonly aoiId: string;
  readonly bounds: Readonly<HorizontalBounds>;
  readonly width: number;
  readonly height: number;
  readonly images: Readonly<Record<MinimapTone, ImageBitmap>>;
  readonly neighborhoods: readonly FullMapNeighborhood[];
  readonly sourceIds: readonly string[];
  dispose(): void;
}

function finiteNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${label} must be finite`);
  }
  return value;
}

function positiveInteger(value: unknown, label: string): number {
  if (!Number.isInteger(value) || (value as number) <= 0) {
    throw new Error(`${label} must be a positive integer`);
  }
  return value as number;
}

function parseNeighborhoods(value: unknown): readonly FullMapNeighborhood[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    throw new Error("Map overview neighborhoods must be an array");
  }
  return value.map((item, index) => {
    if (typeof item !== "object" || item === null || Array.isArray(item)) {
      throw new Error(`neighborhoods[${index}] must be an object`);
    }
    const record = item as Record<string, unknown>;
    const position = record.position as Record<string, unknown> | null;
    if (
      typeof record.id !== "string" || record.id.length === 0 ||
      typeof record.label !== "string" || record.label.length === 0 ||
      typeof record.sourceId !== "string" || record.sourceId.length === 0 ||
      typeof position !== "object" || position === null ||
      Array.isArray(position)
    ) {
      throw new Error(`neighborhoods[${index}] is incomplete`);
    }
    return {
      id: record.id,
      label: record.label,
      position: {
        x: finiteNumber(position.x, `neighborhoods[${index}].position.x`),
        z: finiteNumber(position.z, `neighborhoods[${index}].position.z`),
      },
      sampleCount: positiveInteger(
        record.sampleCount,
        `neighborhoods[${index}].sampleCount`,
      ),
      sourceId: record.sourceId,
    };
  });
}

function parseManifest(value: unknown): FullMapOverviewManifestV1 {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Map overview manifest must be an object");
  }
  const record = value as Record<string, unknown>;
  if (record.schemaVersion !== 1) {
    throw new Error("Unsupported map overview schema");
  }
  if (typeof record.aoiId !== "string" || record.aoiId.length === 0) {
    throw new Error("Map overview aoiId must be a non-empty string");
  }
  if (
    typeof record.bounds !== "object" || record.bounds === null ||
    Array.isArray(record.bounds)
  ) {
    throw new Error("Map overview bounds must be an object");
  }
  const rawBounds = record.bounds as Record<string, unknown>;
  const bounds = {
    minX: finiteNumber(rawBounds.minX, "bounds.minX"),
    minZ: finiteNumber(rawBounds.minZ, "bounds.minZ"),
    maxX: finiteNumber(rawBounds.maxX, "bounds.maxX"),
    maxZ: finiteNumber(rawBounds.maxZ, "bounds.maxZ"),
  };
  if (bounds.maxX <= bounds.minX || bounds.maxZ <= bounds.minZ) {
    throw new Error("Map overview bounds must have positive area");
  }
  if (
    typeof record.images !== "object" || record.images === null ||
    Array.isArray(record.images)
  ) {
    throw new Error("Map overview images must be an object");
  }
  const images = record.images as Record<string, unknown>;
  if (typeof images.light !== "string" || typeof images.dark !== "string") {
    throw new Error("Map overview requires light and dark images");
  }
  if (
    !Array.isArray(record.sourceIds) ||
    !record.sourceIds.every((sourceId) => typeof sourceId === "string")
  ) {
    throw new Error("Map overview sourceIds must be strings");
  }
  return {
    schemaVersion: 1,
    aoiId: record.aoiId,
    bounds,
    width: positiveInteger(record.width, "overview.width"),
    height: positiveInteger(record.height, "overview.height"),
    images: { light: images.light, dark: images.dark },
    neighborhoods: parseNeighborhoods(record.neighborhoods),
    sourceIds: [...record.sourceIds],
  };
}

async function loadBitmap(url: string): Promise<ImageBitmap> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not load map overview ${url} (${response.status})`);
  }
  return createImageBitmap(await response.blob());
}

export async function loadFullMapOverview(
  manifestUrl = FULL_MAP_OVERVIEW_MANIFEST_URL,
): Promise<FullMapOverview | null> {
  try {
    const response = await fetch(manifestUrl);
    if (!response.ok) {
      throw new Error(
        `Could not load map overview manifest (${response.status})`,
      );
    }
    const manifest = parseManifest(await response.json());
    const [light, dark] = await Promise.all([
      loadBitmap(manifest.images.light),
      loadBitmap(manifest.images.dark),
    ]);
    if (
      light.width !== manifest.width || light.height !== manifest.height ||
      dark.width !== manifest.width || dark.height !== manifest.height
    ) {
      light.close();
      dark.close();
      throw new Error("Map overview image dimensions do not match metadata");
    }
    return {
      aoiId: manifest.aoiId,
      bounds: { ...manifest.bounds },
      width: manifest.width,
      height: manifest.height,
      images: { light, dark },
      neighborhoods: manifest.neighborhoods === undefined
        ? []
        : manifest.neighborhoods.map((neighborhood) => ({
            ...neighborhood,
            position: { ...neighborhood.position },
          })),
      sourceIds: [...manifest.sourceIds],
      dispose(): void {
        light.close();
        dark.close();
      },
    };
  } catch (error) {
    console.warn("The instant city overview is unavailable", error);
    return null;
  }
}

export interface FullMapOverviewDrawRects {
  readonly source: Readonly<{ x: number; y: number; width: number; height: number }>;
  readonly destination: Readonly<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
}

export function fullMapOverviewDrawRects(
  overviewBounds: Readonly<HorizontalBounds>,
  visibleBounds: Readonly<HorizontalBounds>,
  imageSize: Readonly<{ width: number; height: number }>,
  canvasSize: Readonly<{ width: number; height: number }>,
): FullMapOverviewDrawRects | null {
  const intersection = {
    minX: Math.max(overviewBounds.minX, visibleBounds.minX),
    minZ: Math.max(overviewBounds.minZ, visibleBounds.minZ),
    maxX: Math.min(overviewBounds.maxX, visibleBounds.maxX),
    maxZ: Math.min(overviewBounds.maxZ, visibleBounds.maxZ),
  };
  if (
    intersection.maxX <= intersection.minX ||
    intersection.maxZ <= intersection.minZ
  ) {
    return null;
  }
  const overviewWidth = overviewBounds.maxX - overviewBounds.minX;
  const overviewHeight = overviewBounds.maxZ - overviewBounds.minZ;
  const visibleWidth = visibleBounds.maxX - visibleBounds.minX;
  const visibleHeight = visibleBounds.maxZ - visibleBounds.minZ;
  return {
    source: {
      x: (intersection.minX - overviewBounds.minX) / overviewWidth *
        imageSize.width,
      y: (intersection.minZ - overviewBounds.minZ) / overviewHeight *
        imageSize.height,
      width: (intersection.maxX - intersection.minX) / overviewWidth *
        imageSize.width,
      height: (intersection.maxZ - intersection.minZ) / overviewHeight *
        imageSize.height,
    },
    destination: {
      x: (intersection.minX - visibleBounds.minX) / visibleWidth *
        canvasSize.width,
      y: (intersection.minZ - visibleBounds.minZ) / visibleHeight *
        canvasSize.height,
      width: (intersection.maxX - intersection.minX) / visibleWidth *
        canvasSize.width,
      height: (intersection.maxZ - intersection.minZ) / visibleHeight *
        canvasSize.height,
    },
  };
}
